import { useState, type ReactNode, type SyntheticEvent } from 'react';
import MediaThumbnail from './MediaThumbnail';
import ProtectedImage from '../media/ProtectedImage';
import './FittedThumbnail.scss';

interface FittedThumbnailProps {
  /** Resolved URL, as for MediaThumbnail; '' / null / undefined = none. */
  src: string | null | undefined;
  alt: string;
  /** pic-service processing_state, passed on to MediaThumbnail. */
  state?: 'pending' | 'ready' | 'failed';
  /** The picture's pixel dimensions when the payload has them (pic-service sends
   *  them): the box gets its final size before the image loads. */
  width?: number | null;
  height?: number | null;
  /** Draw two sheets peeking out above the picture, as if more photos were
   *  stacked behind it (an album cover). The host leaves room above the box. */
  stacked?: boolean;
  /** Draw the picture through moving screen noise (ProtectedImage) instead of a plain <img>. */
  protect?: boolean;
  /** On the box; the host sizes it by width. */
  className?: string;
  /** Overlays (e.g. a video play badge), positioned over the picture itself. */
  children?: ReactNode;
}

const ratioOf = (w: number | null | undefined, h: number | null | undefined) =>
  w && h && w > 0 && h > 0 ? w / h : null;

/**
 * A media thumbnail fitted whole, never cropped, into a square as wide as the
 * box: scaled until its longer edge meets the square and centred horizontally.
 * The box is only as tall as the picture (a landscape's box is shorter than
 * the square, a portrait's is the square), so a host grid that aligns its items
 * to the end of their row makes each row as tall as its tallest picture, every
 * picture standing on the same line with its caption right under it — and a
 * landscape and a portrait of reciprocal ratios get the same area.
 *
 * The frame (rounded corners, hairline, any hover effect the host adds on
 * `.fitted-thumbnail-frame`) hugs the picture. Its ratio comes from
 * `width`/`height`, else from the image once it loads (the box is square until
 * then); MediaThumbnail's processing / broken fallbacks fill the box.
 */
const FittedThumbnail = ({
  src,
  alt,
  state,
  width,
  height,
  stacked = false,
  protect = false,
  className,
  children,
}: FittedThumbnailProps) => {
  // The loaded image has the last word: it is what is actually on screen.
  const [measured, setMeasured] = useState<number | null>(null);
  const ratio = src ? (measured ?? ratioOf(width, height)) : null;

  const handleLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    setMeasured(ratioOf(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight));
  };
  const handleProtectedLoad = (size: { width: number; height: number }) => {
    setMeasured(ratioOf(size.width, size.height));
  };

  // The frame and the sheets behind it share their width.
  const frameWidth = ratio ? { width: `${Math.min(1, ratio) * 100}%` } : undefined;

  return (
    <div
      className={['fitted-thumbnail', className].filter(Boolean).join(' ')}
      style={ratio ? { aspectRatio: String(Math.max(1, ratio)) } : undefined}
    >
      {stacked && (
        <>
          <div className="fitted-thumbnail-sheet is-back" style={frameWidth} aria-hidden />
          <div className="fitted-thumbnail-sheet is-middle" style={frameWidth} aria-hidden />
        </>
      )}
      <div className={`fitted-thumbnail-frame${ratio ? ' is-sized' : ''}`} style={frameWidth}>
        {protect ? (
          <ProtectedImage src={src} alt={alt} state={state} className="fitted-thumbnail-img" onLoad={handleProtectedLoad} />
        ) : (
          <MediaThumbnail src={src} alt={alt} state={state} className="fitted-thumbnail-img" onLoad={handleLoad} />
        )}
        {children}
      </div>
    </div>
  );
};

export default FittedThumbnail;
